/**
 * 台本取り込みレポートの再取得トリガ (docs/features/scenario-script-import-plan.md §6.6.7)。
 *
 * BEは確認対象 (シーン本文・話者・尺・順序・作成・削除、登場人物、motion-v2変換) の更新に成功すると
 * 自動照合を失効させる。FE側はシナリオ APIラッパ (api.ts) が成功後にここへ通知し、
 * シナリオの state を持つ層 (useImportReportAutoRefresh) が取り込み由来のシナリオだけ
 * GET /import-report で最新のレポートを取り直す。APIラッパは origin を知らないので、判定は購読側で行う。
 */

/** 確認対象が変わった可能性のある更新。scenarioId が分からない経路 (シーンIDだけの API) は sceneIds で渡す。 */
export type ImportContentChange = {
  scenarioId?: string | null;
  sceneIds?: string[];
};

type Listener = (change: ImportContentChange) => void;

const listeners = new Set<Listener>();

export function subscribeImportContentChanged(listener: Listener): () => void {
  listeners.add(listener);
  return () => { listeners.delete(listener); };
}

/** 更新の成功後に呼ぶ。購読側の失敗で本体の更新を失敗させない。 */
export function notifyImportContentChanged(change: ImportContentChange): void {
  for (const listener of [...listeners]) {
    try {
      listener(change);
    } catch (err) {
      console.warn('[importReportRefresh] listener failed', err);
    }
  }
}

/**
 * update_scene で更新しても確認対象に影響しない列 (素材の採用・生成物)。
 * BE の `_IMPORT_VERIFICATION_NEUTRAL_SCENE_COLUMNS` (backend/services/scenario/crud/scenario_crud.py) と揃える。
 */
export const IMPORT_VERIFICATION_NEUTRAL_SCENE_FIELDS: ReadonlySet<string> = new Set([
  'video_material_id', 'image_material_id', 'last_frame_material_id', 'last_frame_url',
  // シーン単位のフレーム生成調整 (scene-frame-generation-tuning-plan.md §6.8)
  'frame_additional_prompt', 'frame_reference_overrides',
  'preset_image_material_id', 'preset_image_url', 'narration_material_id', 'narration_voice_id',
  'sound_logo_material_id', 'audio_track_type',
]);

/** update_scene の payload が確認対象に影響しうるか (中立列だけの更新なら false)。undefined の値は送られないので数えない。 */
export function scenePayloadAffectsImportVerification(payload: Record<string, unknown> | null | undefined): boolean {
  if (!payload) return false;
  return Object.entries(payload).some(([key, value]) => value !== undefined && !IMPORT_VERIFICATION_NEUTRAL_SCENE_FIELDS.has(key));
}
